class HondenplaneetTheme {
  constructor() {
    this.root = document.documentElement;
    this.drawer = document.querySelector('[data-hp-cart-drawer]');
    this.menu = document.querySelector('[data-hp-mobile-menu]');
    this.moneyFormat = window.hpShopify?.moneyFormat || '€{{amount_with_comma_separator}}';
    this.bind();
    this.refreshCart();
    this.initProducts();
    this.initTestimonials();
    this.initFaqs();
    this.initFilters();
  }

  bind() {
    document.addEventListener('click', (event) => {
      const menuOpen = event.target.closest('[data-hp-menu-open]');
      const menuClose = event.target.closest('[data-hp-menu-close]');
      const cartOpen = event.target.closest('[data-hp-cart-open]');
      const cartClose = event.target.closest('[data-hp-cart-close]');
      const backdrop = event.target.closest('[data-hp-cart-backdrop]');
      const quickAdd = event.target.closest('[data-quick-add]');
      const cartChange = event.target.closest('[data-cart-change]');
      const cartRemove = event.target.closest('[data-cart-remove]');

      if (menuOpen) this.openMenu();
      if (menuClose) this.closeMenu();
      if (cartOpen) this.openCart();
      if (cartClose || backdrop) this.closeCart();
      if (quickAdd) this.quickAdd(quickAdd.dataset.quickAdd, quickAdd);
      if (cartChange) this.changeCart(cartChange.dataset.cartChange, Number(cartChange.dataset.quantity));
      if (cartRemove) this.changeCart(cartRemove.dataset.cartRemove, 0);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeMenu();
        this.closeCart();
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('.hp-product-form');
      if (!form) return;
      event.preventDefault();
      this.addForm(form);
    });
  }

  openMenu() {
    if (!this.menu) return;
    this.menu.classList.add('is-open');
    this.menu.setAttribute('aria-hidden', 'false');
    document.querySelector('[data-hp-menu-open]')?.setAttribute('aria-expanded', 'true');
    this.menu.querySelector('a,button')?.focus();
  }

  closeMenu() {
    if (!this.menu) return;
    this.menu.classList.remove('is-open');
    this.menu.setAttribute('aria-hidden', 'true');
    document.querySelector('[data-hp-menu-open]')?.setAttribute('aria-expanded', 'false');
  }

  openCart() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.drawer.querySelector('.cart-drawer')?.setAttribute('aria-hidden', 'false');
    this.drawer.querySelector('[data-hp-cart-close]')?.focus();
  }

  closeCart() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.querySelector('.cart-drawer')?.setAttribute('aria-hidden', 'true');
  }

  async request(url, options) {
    const response = await fetch(url, options);
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Er ging iets mis. Probeer het opnieuw.');
    return data;
  }

  async addForm(form) {
    const button = form.querySelector('[type="submit"]');
    const message = form.querySelector('[data-hp-form-message]');
    button.disabled = true;
    if (message) message.textContent = '';
    try {
      const formData = new FormData(form);
      const selectionScript = form.closest('[data-hp-product]')?.querySelector('[data-product-selection]');
      if (selectionScript) {
        const selection = JSON.parse(selectionScript.textContent);
        if (selection.variant?.id) formData.set('id', String(selection.variant.id));
      }
      if (!formData.get('id')) throw new Error('Selecteer eerst een geldige productvariant.');
      await this.request(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', body: formData });
      if (message) {
        message.dataset.status = 'success';
        message.textContent = message.dataset.success || 'Toegevoegd aan je winkelmand.';
      }
      await this.refreshCart();
      this.openCart();
    } catch (error) {
      if (message) {
        message.dataset.status = 'error';
        message.textContent = error.message;
      }
    } finally {
      button.disabled = false;
    }
  }

  async quickAdd(id, button) {
    button.disabled = true;
    try {
      await this.request(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }) });
      await this.refreshCart();
      this.openCart();
    } catch (error) {
      window.alert(error.message);
    } finally {
      button.disabled = false;
    }
  }

  async refreshCart() {
    if (!this.drawer) return;
    try {
      const cart = await this.request(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      document.querySelectorAll('[data-hp-cart-count]').forEach((element) => {
        element.textContent = cart.item_count > 99 ? '99+' : String(cart.item_count);
        element.hidden = false;
      });
      this.renderCart(cart);
    } catch (error) {
      const items = this.drawer.querySelector('[data-hp-cart-items]');
      if (items) items.innerHTML = `<p class="hp-cart-error">${this.escape(error.message)}</p>`;
    }
  }

  async changeCart(key, quantity) {
    try {
      await this.request(`${window.Shopify?.routes?.root || '/'}cart/change.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      await this.refreshCart();
    } catch (error) {
      window.alert(error.message);
    }
  }

  renderCart(cart) {
    const items = this.drawer.querySelector('[data-hp-cart-items]');
    const total = this.drawer.querySelector('[data-hp-cart-total]');
    const footer = this.drawer.querySelector('[data-hp-cart-footer]');
    if (!items || !total || !footer) return;
    total.textContent = this.formatMoney(cart.total_price);
    footer.hidden = cart.item_count === 0;
    if (!cart.items.length) {
      const emptyTemplate = document.querySelector('[data-hp-empty-cart-template]');
      items.innerHTML = emptyTemplate?.innerHTML || '';
      return;
    }
    items.innerHTML = cart.items.map((item) => {
      const image = item.featured_image?.url ? `<img src="${this.escape(item.featured_image.url)}&width=240" alt="${this.escape(item.product_title)}" loading="lazy">` : '<div class="cart-item__image-placeholder"></div>';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p>${this.escape(item.variant_title)}</p>` : '';
      const properties = Object.entries(item.properties || {}).filter(([key, value]) => value && !key.startsWith('_')).map(([key, value]) => `<span>${this.escape(key)}: ${this.escape(typeof value === 'string' && value.includes('/uploads/') ? 'Bestand toegevoegd' : value)}</span>`).join('');
      return `<article class="cart-item">${image}<div><h3>${this.escape(item.product_title)}</h3>${variant}${properties ? `<p class="cart-item__properties">${properties}</p>` : ''}<strong>${this.formatMoney(item.final_line_price)}</strong><div class="quantity"><button type="button" data-cart-change="${this.escape(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Verminder aantal">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${this.escape(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Verhoog aantal">+</button></div></div><button class="remove-item" type="button" data-cart-remove="${this.escape(item.key)}" aria-label="Verwijder ${this.escape(item.product_title)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 6 6 18M6 6l12 12"/></svg></button></article>`;
    }).join('');
  }

  initProducts() {
    document.querySelectorAll('[data-hp-product]').forEach((scope) => {
      const selectionScript = scope.querySelector('[data-product-selection]');
      const variantInput = scope.querySelector('.hp-product-form [name="id"]');
      if (selectionScript && variantInput) {
        try {
          const selection = JSON.parse(selectionScript.textContent);
          variantInput.value = selection.variant?.id || '';
        } catch (error) {
          variantInput.value = '';
        }
      }
      this.ensureDesignColor(scope);
      this.syncCaseTypeAvailability(scope);
      this.applyCaseDesign(scope);
      if (scope.dataset.initialized) return;
      scope.dataset.initialized = 'true';
      scope.addEventListener('change', (event) => {
        if (event.target.matches('[data-design-color-input]')) {
          this.ensureDesignColor(scope, event.target.value);
          this.applyCaseDesign(scope);
        } else if (event.target.matches('[data-option-index]')) {
          this.loadProductSelection(scope, new URL(scope.dataset.productUrl, location.origin), {
            optionIds: this.selectedOptionIds(scope), focusName: event.target.name, focusValue: event.target.value
          });
        }
        if (event.target.matches('[data-name-toggle]')) {
          const field = scope.querySelector('[data-name-field]');
          field.hidden = !event.target.checked;
          field.querySelector('input').disabled = !event.target.checked;
        }
        if (event.target.matches('input[type="file"]')) {
          const label = scope.querySelector('[data-file-name]');
          if (label) label.textContent = event.target.files?.[0]?.name || label.dataset.default;
        }
      });
      scope.addEventListener('click', (event) => {
        const switcher = event.target.closest('[data-product-switch]');
        if (switcher && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) {
          event.preventDefault();
          if (switcher.getAttribute('aria-current') === 'true') return;
          this.loadProductSelection(scope, new URL(switcher.href), { sibling: true, focusHref: switcher.getAttribute('href') });
        }
        const opener = event.target.closest('[data-gallery-open]');
        const dialog = scope.querySelector('[data-image-dialog]');
        if (opener && dialog) {
          const img = dialog.querySelector('[data-dialog-image]');
          img.src = opener.dataset.galleryOpen;
          img.alt = opener.querySelector('img')?.alt || '';
          const normalized = opener.classList.contains('hp-product-image-button--normalized');
          dialog.classList.toggle('hp-image-dialog--normalized', normalized);
          ['--hp-image-height', '--hp-image-x', '--hp-image-y'].forEach((property) => {
            if (normalized) dialog.style.setProperty(property, opener.style.getPropertyValue(property));
            else dialog.style.removeProperty(property);
          });
          dialog._opener = opener;
          dialog.showModal();
        }
        if (dialog && (event.target.closest('[data-gallery-close]') || event.target === dialog)) {
          dialog.close();
          dialog._opener?.focus({ preventScroll: true });
        }
      });
    });
    if (!this.productHistoryBound) {
      this.productHistoryBound = true;
      window.addEventListener('popstate', () => {
        const scope = document.querySelector('[data-hp-product]');
        if (scope && location.pathname.includes('/products/')) {
          this.loadProductSelection(scope, new URL(location.href), { history: false });
        }
      });
      document.addEventListener('shopify:section:load', () => this.initProducts());
    }
  }

  selectedOptionIds(scope) {
    return [...scope.querySelectorAll('[data-option-position]:not([data-design-color-group])')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((group) => {
        const input = group.querySelector('select')?.selectedOptions[0] || group.querySelector('input:checked');
        return input?.dataset.optionValueId;
      }).filter(Boolean);
  }

  async loadProductSelection(scope, productUrl, options = {}) {
    this.productRequest?.abort();
    const controller = new AbortController();
    this.productRequest = controller;
    const sectionId = scope.dataset.sectionId;
    const submit = scope.querySelector('[type="submit"]');
    const wasDisabled = submit.disabled;
    submit.disabled = true;
    scope.setAttribute('aria-busy', 'true');
    const message = scope.querySelector('[data-hp-form-message]');
    const designColor = scope.querySelector('[data-design-color-input]:checked')?.value;
    if (message) message.textContent = '';
    const selected = new Map([...scope.querySelectorAll('[data-option-name]')].map((group) => [
      group.dataset.optionName.toLowerCase(),
      (group.querySelector('select') || group.querySelector('input:checked'))?.value
    ]));
    const requestSection = async (url, id) => {
      const requestUrl = new URL(url);
      requestUrl.searchParams.set('section_id', id);
      const previewId = new URL(location.href).searchParams.get('preview_theme_id');
      if (previewId) requestUrl.searchParams.set('preview_theme_id', previewId);
      const response = await fetch(requestUrl, { signal: controller.signal });
      if (!response.ok) throw new Error(scope.dataset.selectionError);
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    };
    try {
      if (options.optionIds) {
        productUrl.searchParams.delete('variant');
        productUrl.searchParams.set('option_values', options.optionIds.join(','));
      }
      let html = await requestSection(productUrl, sectionId);
      let next = html.querySelector('[data-hp-product]');
      if (!next) throw new Error(scope.dataset.selectionError);
      if (options.sibling) {
        let changed = false;
        [...next.querySelectorAll('[data-option-name]:not([data-design-color-group])')].forEach((group) => {
          const previous = selected.get(group.dataset.optionName.toLowerCase());
          if (!previous) return;
          const controls = [...group.querySelectorAll('option, input[type="radio"]')];
          const candidate = controls.find((input) => input.value.toLowerCase() === previous.toLowerCase());
          if (!candidate) return;
          if (candidate.tagName === 'OPTION') {
            if (!candidate.selected) changed = true;
            candidate.selected = true;
          } else {
            if (!candidate.checked) changed = true;
            controls.forEach((input) => { input.checked = input === candidate; });
          }
        });
        if (changed) {
          productUrl.searchParams.set('option_values', this.selectedOptionIds(next).join(','));
          html = await requestSection(productUrl, sectionId);
          next = html.querySelector('[data-hp-product]');
          if (!next) throw new Error(scope.dataset.selectionError);
        }
      }
      if (controller.signal.aborted) return;
      // Move the original upload node so the browser retains the user's selected file.
      scope.querySelectorAll('input[type="file"]').forEach((input) => {
        const target = [...next.querySelectorAll('input[type="file"]')].find((candidate) => candidate.name === input.name);
        if (target) target.replaceWith(input);
      });
      scope.querySelectorAll('[name^="properties["]:not([type="file"])').forEach((input) => {
        if (input.matches('[data-design-color-input]')) return;
        const target = [...next.querySelectorAll('[name^="properties["]')].find((candidate) => candidate.name === input.name);
        if (!target) return;
        target.value = input.value;
        target.checked = input.checked;
        target.disabled = input.disabled;
      });
      this.ensureDesignColor(next, designColor);
      const toggle = next.querySelector('[data-name-toggle]');
      if (toggle) next.querySelector('[data-name-field]').hidden = !toggle.checked;
      const upload = next.querySelector('input[type="file"]');
      if (upload?.files?.length) next.querySelector('[data-file-name]').textContent = upload.files[0].name;
      const active = document.activeElement;
      const focusTop = active && scope.contains(active) ? active.getBoundingClientRect().top : null;
      scope.replaceWith(next);
      this.initProducts();
      const selection = JSON.parse(next.querySelector('[data-product-selection]').textContent);
      const displayUrl = new URL(productUrl);
      displayUrl.searchParams.delete('section_id');
      if (selection.variant) {
        displayUrl.searchParams.delete('option_values');
        displayUrl.searchParams.set('variant', selection.variant.id);
      }
      if (options.history !== false) {
        if (options.sibling) history.pushState({}, '', displayUrl);
        else history.replaceState({}, '', displayUrl);
      }
      document.title = selection.title + ' – Hondenplaneet.be';
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.href = new URL(selection.url, location.origin).href;
      const focusTarget = options.focusName
        ? [...next.querySelectorAll('[data-option-index]')].find((input) => input.name === options.focusName && (input.tagName === 'SELECT' || input.value === options.focusValue))
        : [...next.querySelectorAll('[data-product-switch]')].find((a) => a.getAttribute('href') === options.focusHref);
      if (focusTarget) {
        focusTarget.focus({ preventScroll: true });
        if (focusTop !== null) window.scrollBy({ top: focusTarget.getBoundingClientRect().top - focusTop, behavior: 'instant' });
      }
      if (options.sibling || options.history === false) {
        for (const recommendations of document.querySelectorAll('[data-hp-related]')) {
          const relatedHtml = await requestSection(productUrl, recommendations.dataset.sectionId);
          const updated = relatedHtml.querySelector('[data-hp-related]');
          if (updated && !controller.signal.aborted) recommendations.replaceWith(updated);
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      const current = document.querySelector('[data-hp-product]');
      const errorMessage = current?.querySelector('[data-hp-form-message]');
      if (errorMessage) {
        errorMessage.dataset.status = 'error';
        errorMessage.textContent = scope.dataset.selectionError;
      }
      // Prevent purchase of the previously selected variant after a failed update.
      if (scope.isConnected) submit.disabled = options.sibling ? wasDisabled : true;
    } finally {
      if (!controller.signal.aborted) scope.removeAttribute('aria-busy');
    }
  }

  ensureDesignColor(scope, preferredColor) {
    const group = scope.querySelector('[data-design-color-group]');
    if (!group) return;
    const inputs = [...group.querySelectorAll('[data-design-color-input]')];
    if (!inputs.length) return;
    let selected = preferredColor
      ? inputs.find((input) => input.value.toLowerCase() === preferredColor.toLowerCase())
      : inputs.find((input) => input.checked);
    selected ||= inputs[0];
    inputs.forEach((input) => { input.checked = input === selected; });
    const label = group.querySelector('[data-selected-option]');
    if (label) label.textContent = selected.value;
  }

  syncCaseTypeAvailability(scope) {
    if (scope.dataset.caseBrand !== 'iphone') return;
    const selectedType = scope.querySelector('input[name="options[Type Case]"]:checked')?.value || '';
    const modelSelect = scope.querySelector('select[name="options[Model]"]');
    if (!modelSelect) return;
    const magsafeModels = new Set([
      'iphone-14', 'iphone-14-plus', 'iphone-14-pro', 'iphone-14-pro-max',
      'iphone-15', 'iphone-15-plus', 'iphone-15-pro', 'iphone-15-pro-max',
      'iphone-16', 'iphone-16-plus', 'iphone-16-pro', 'iphone-16-pro-max',
      'iphone-17', 'iphone-17-air', 'iphone-17-pro', 'iphone-17-pro-max',
      'iphone-18-pro', 'iphone-18-pro-max'
    ]);
    const slug = (value) => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const requiresMagsafe = selectedType.toLowerCase().includes('magsafe');
    [...modelSelect.options].forEach((option) => {
      option.disabled = requiresMagsafe && !magsafeModels.has(slug(option.value));
    });
  }

  applyCaseDesign(scope) {
    if (!['iphone', 'samsung'].includes(scope.dataset.caseBrand)) return;
    const gallery = scope.querySelector('[data-case-mockup-template]');
    const image = scope.querySelector('[data-case-mockup-image]');
    const button = scope.querySelector('[data-case-mockup-button]');
    const modelGroup = [...scope.querySelectorAll('[data-option-name]')]
      .find((group) => group.dataset.optionName.toLowerCase() === 'model');
    const modelInput = modelGroup?.querySelector('select')?.selectedOptions[0] || modelGroup?.querySelector('input:checked');
    const colorInput = scope.querySelector('[data-design-color-input]:checked');
    if (!gallery || !image || !button || !modelInput || !colorInput) return;
    const slug = (value) => value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const filename = `hp-mock-${scope.dataset.caseStyle}-${scope.dataset.caseBrand}-${slug(modelInput.value)}-${colorInput.dataset.designColorKey || slug(colorInput.value)}.webp`;
    const url = gallery.dataset.caseMockupTemplate.replace(/hp-mock-[^/?]+\.webp/, filename);
    image.src = url;
    image.removeAttribute('srcset');
    image.alt = `${scope.querySelector('h1')?.textContent?.trim() || 'Phone case'} – ${modelInput.value} – ${colorInput.value}`;
    button.dataset.galleryOpen = url;
    const summary = scope.querySelector('[data-case-selection-summary]');
    if (summary) {
      const values = [...scope.querySelectorAll('[data-option-name]')].map((group) => {
        const input = group.querySelector('select')?.selectedOptions[0] || group.querySelector('input:checked');
        return input?.value;
      }).filter(Boolean);
      summary.textContent = values.join(' · ');
    }
  }

  initTestimonials() {
    document.querySelectorAll('[data-testimonials]').forEach((scope) => {
      const slides = [...scope.querySelectorAll('[data-testimonial-slide]')];
      const dots = [...scope.querySelectorAll('[data-testimonial-dot]')];
      if (!slides.length) return;
      let index = 0;
      const show = (next) => {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, i) => { slide.hidden = i !== index; });
        dots.forEach((dot, i) => {
          const dotIndex = i % slides.length;
          dot.classList.toggle('active', dotIndex === index);
          dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
        });
      };
      scope.querySelector('[data-testimonial-prev]')?.addEventListener('click', () => show(index - 1));
      scope.querySelector('[data-testimonial-next]')?.addEventListener('click', () => show(index + 1));
      dots.forEach((dot, i) => dot.addEventListener('click', () => show(i % slides.length)));
      show(0);
    });
  }

  initFaqs() {
    document.querySelectorAll('[data-faq-item]').forEach((item) => {
      const button = item.querySelector('button');
      button?.addEventListener('click', () => {
        const open = item.classList.toggle('open');
        button.setAttribute('aria-expanded', String(open));
      });
    });
  }

  initFilters() {
    document.querySelectorAll('[data-product-filter]').forEach((scope) => {
      const cards = [...scope.querySelectorAll('[data-product-tags]')];
      const count = scope.querySelector('[data-product-count]');
      scope.querySelectorAll('[data-filter]').forEach((button) => button.addEventListener('click', () => {
        const filter = button.dataset.filter;
        let visible = 0;
        scope.querySelectorAll('[data-filter]').forEach((candidate) => candidate.classList.toggle('active', candidate === button));
        cards.forEach((card) => {
          const show = filter === 'all' || card.dataset.productTags.includes(filter);
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (count) count.textContent = `${visible} producten`;
      }));
    });
  }

  formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || 'nl-BE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(value);
  }

  escape(value) {
    const div = document.createElement('div');
    div.textContent = String(value ?? '');
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }
}

const initializeHondenplaneetTheme = () => {
  if (!window.hondenplaneetTheme) window.hondenplaneetTheme = new HondenplaneetTheme();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeHondenplaneetTheme, { once: true });
} else {
  initializeHondenplaneetTheme();
}
